import { useId, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import type { TrialBalanceReport } from "@/api/types";
import "./ReportsTrialBalance.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/** Today, UTC — same server-clock default `TrialBalanceReportEndpoints`' own `asOf`
 *  defaulting uses, restated here only for the form's own initial value. */
function defaultAsOf(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Cents-precise, no currency symbol — matches `TrialBalanceCsv`'s own two-decimal,
 *  no-`$` number face, so the on-screen figure and the downloaded file always read the
 *  same amount the same way. */
function formatAmount(amount: number): string {
  return amount.toFixed(2);
}

/**
 * Trial balance — Reports' worksheet of every account's actual debit/credit balance as
 * of a date the owner chooses, across the WHOLE tenant. ef-054-3, ADR 0009 §6 ("Reports
 * is one picker" — a report renders inside the template at `/reports/{slug}`, no area
 * tab strip of its own; "All reports" below is the only way back).
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Apply IS PRESSED — same "no guessed window" discipline
 * `ReportsAging.tsx`'s own remarks give, restated here for the identical reason: a
 * balance read against a date nobody asked for is a number a farmer did not request. The
 * As of input still carries today's date as a sensible default so the form is never an
 * empty field.
 * </p>
 *
 * <p>
 * NO ENTITY PICKER, NO ACCOUNT PICKER — a trial balance is tenant-wide by definition
 * (`TrialBalanceReportRepository`'s own remarks); there is nothing to scope it to.
 * </p>
 *
 * <p>
 * THE MISMATCH BANNER IS COMPUTED HERE, NOT SERVED — the response carries no `balanced`
 * boolean (final field shape, `TrialBalanceReportContracts.cs`'s own remarks); this
 * screen compares `totalDebits`/`totalCredits` itself, to the cent, and says so only when
 * they actually disagree.
 * </p>
 */
export function ReportsTrialBalance() {
  const asOfId = useId();

  const [asOf, setAsOf] = useState(defaultAsOf);

  const [report, setReport] = useState<TrialBalanceReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const canSubmit = asOf !== "" && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getTrialBalanceReport({ asOf });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the trial balance."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the
   * report, same reasoning `ReportsAging.tsx`'s identical download gives: paying for the
   * whole CSV body on every date change would be wasted work for the visits that never
   * press the button.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!canSubmit) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getTrialBalanceCsv({ asOf });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsAging.tsx`'s identical download
      // follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-trial-balance-${asOf}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the trial balance file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device `ReportsAging.tsx`'s own handler already uses rather
    // than threading a `navigate` prop through every screen this job touches: pushState
    // doesn't fire popstate on its own, so App.tsx's existing listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const rows = report?.rows ?? [];
  const isEmpty = status === "ready" && rows.length === 0;

  // To-the-cent comparison — the server never rounds beyond two decimals (numeric(14,2)),
  // so a plain inequality on the JSON numbers is exact, not a floating-point trap.
  const isMismatched = report != null && report.totalDebits !== report.totalCredits;

  return (
    <main className="trial-balance">
      <PageHeader
        title="Trial balance"
        subtitle="Every account's actual debit and credit balance as of a date"
      />

      <a href="/reports" className="trial-balance__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="trial-balance__panel"
        aria-labelledby="trial-balance-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="trial-balance-heading" className="trial-balance__heading">
          Worksheet
        </h2>

        <form className="trial-balance__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="trial-balance__field">
            <label htmlFor={asOfId}>As of</label>
            <input
              id={asOfId}
              type="date"
              value={asOf}
              onChange={(event) => setAsOf(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary trial-balance__apply" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Apply"}
          </button>
        </form>

        <Why>
          Every account whose activity nets to zero as of <strong>As of</strong> is left
          off — only accounts with a real balance appear. A row's balance shows in
          whichever column it actually falls, debit or credit.
        </Why>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the trial balance."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="No account has a balance as of this date."
            detail="Every account's debit and credit activity nets to zero."
          />
        )}

        {status === "ready" && rows.length > 0 && (
          <>
            {isMismatched && report && (
              <StateMessage
                tone="error"
                title="Debits and credits do not match."
                detail={`Total debits ${formatAmount(report.totalDebits)} vs. total credits ${formatAmount(report.totalCredits)}.`}
              />
            )}

            <div
              className="trial-balance__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="trial-balance-heading"
            >
              <table className="trial-balance__table ruled-rows">
                <caption className="trial-balance__caption">
                  {rows.length} {rows.length === 1 ? "account" : "accounts"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Number</th>
                    <th scope="col">Name</th>
                    <th scope="col">Kind</th>
                    <th scope="col" className="trial-balance__num">Debit</th>
                    <th scope="col" className="trial-balance__num">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.accountId}>
                      <th scope="row" className="trial-balance__number">{row.number}</th>
                      <td>{row.name}</td>
                      <td>{row.kind}</td>
                      <td className="trial-balance__num trial-balance__amount">
                        {formatAmount(row.debit)}
                      </td>
                      <td className="trial-balance__num trial-balance__amount">
                        {formatAmount(row.credit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="trial-balance__totals">
                    <th scope="row" colSpan={3}>Totals</th>
                    <td className="trial-balance__num trial-balance__amount">
                      {report && formatAmount(report.totalDebits)}
                    </td>
                    <td className="trial-balance__num trial-balance__amount">
                      {report && formatAmount(report.totalCredits)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="trial-balance__export">
              <h3 className="trial-balance__export-title">Trial balance for your records</h3>
              <p className="trial-balance__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary trial-balance__export-btn"
                disabled={csvState.kind === "busy" || !canSubmit}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="trial-balance__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="trial-balance__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
